import {
  ExternalBankAccountList,
  GetMissingFieldsLevelParameter,
  InternalAccountList,
  OnboardingMissingFields,
  PayoutSettings
} from "@wingspanhq/wingspan-api-sdk";
import { WSQueryResult } from "@ws-react-query";
import { WSServiceError } from "../../../utils/serviceHelper";
import { selectorExternalBankAccountFullName } from "@selectors/selectorExternalBankAccountFullName";
import { selectorPayoutSettingsDefaultDestination } from "@selectors/selectorPayoutSettingsDefaultDestination";
import { selectorActiveBankingAccount } from "../../../shared/selectors/selectorActiveBankingAccount";
import { selectorIsPlatformBalanceAccount } from "../../PlatformBalance/selectors/selectorIsPlatformBalanceAccount";
import { getRequireAdditionalInformation } from "../components/FormCreateInternalAccount/utils";
import { PayoutMethod } from "../steps/PayoutMethod";
import { OnboardingContext, OnboardingModulePayoutMethod } from "../types";
import { useFeatureFlags } from "../../../query/hooks/useFeatureFlags";
import {
  buildOnboardingStep,
  UseCreateOnboardingStep,
  useOnboardingStepQueryConfig
} from "../components/OnboardingFlowUtils";
import { useQueryOwnPayoutSettings } from "@hooks/useQueryOwnPayoutSettings";
import {
  queriesExternalBankAccounts,
  queriesInternalAccounts,
  queriesOnboarding
} from "@query";
import {
  FlowStep,
  FlowStepStatus
} from "../../../shared/components/FlowSwitch/types";

const getStatus = (
  payoutSettings?: PayoutSettings,
  internalAccounts?: InternalAccountList,
  bankingMissingData?: OnboardingMissingFields,
  featureFlags?: ReturnType<typeof useFeatureFlags>["data"]
): FlowStepStatus => {
  const hasPayoutDestination =
    !!payoutSettings &&
    !!selectorPayoutSettingsDefaultDestination(payoutSettings);

  const bankingAccount = internalAccounts?.data?.find(
    internalAccount => internalAccount.type === "Banking"
  );

  const hasActiveBankingAccount = bankingAccount?.status === "Active";

  if (hasPayoutDestination || hasActiveBankingAccount) {
    return "Complete";
  }

  const hasPendingBankingAccount = bankingAccount?.status === "Pending";

  if (hasPendingBankingAccount) {
    const requireAdditionalInformation = getRequireAdditionalInformation(
      bankingAccount,
      bankingMissingData,
      featureFlags
    );

    if (requireAdditionalInformation) {
      // Keep going with onboarding, we will ask for additional info
      return "None";
    }

    return "Pending";
  }

  return "None";
};

export const getPayoutMethodStepConfig = (
  _: OnboardingModulePayoutMethod,
  __: OnboardingContext,
  payoutSettings?: PayoutSettings,
  externalBankAccounts?: ExternalBankAccountList,
  internalAccounts?: InternalAccountList,
  bankingMissingData?: OnboardingMissingFields,
  featureFlags?: ReturnType<typeof useFeatureFlags>["data"]
): FlowStep => {
  const slug = "payout";

  const status = getStatus(
    payoutSettings,
    internalAccounts,
    bankingMissingData,
    featureFlags
  );

  const methodText = getMethodText(
    payoutSettings,
    externalBankAccounts,
    internalAccounts
  );

  return {
    title: "Payout method",
    slug,
    component: PayoutMethod,

    status,
    result: {
      Complete: {
        title: "Your payout method was added",
        description: `The payout method was added and your payments will be deposited into your ${methodText}. Click continue for the next step.`
      },
      Pending: {
        title: "Your payout method is almost set up",
        description:
          "Please, go to the Wallet and follow instructions to complete setup"
      }
    }
  };
};

const getMethodText = (
  payoutSettings?: PayoutSettings,
  externalBankAccounts?: ExternalBankAccountList,
  internalAccounts?: InternalAccountList
) => {
  const defaultDestination = payoutSettings
    ? selectorPayoutSettingsDefaultDestination(payoutSettings)
    : undefined;

  const matchingAccount = externalBankAccounts?.data?.find(
    externalBankAccount => externalBankAccount.id === defaultDestination?.id
  );

  if (matchingAccount) {
    return (
      selectorExternalBankAccountFullName(matchingAccount) +
      " account via direct deposit (ACH)"
    );
  }

  const platformBalanceInternalAccount = payoutSettings
    ? internalAccounts?.data?.find(internalAccount =>
        selectorIsPlatformBalanceAccount(internalAccount, payoutSettings)
      )
    : undefined;

  if (platformBalanceInternalAccount) {
    return "Platform balance";
  }

  const activeBankingAccount = internalAccounts?.data?.find(
    selectorActiveBankingAccount
  );

  if (activeBankingAccount) {
    return "Wingspan Wallet";
  }
};

export const usePayoutMethodStep: UseCreateOnboardingStep<
  OnboardingModulePayoutMethod
> = modules => {
  const config = useOnboardingStepQueryConfig(modules, "payout_method");
  const queryPayoutSettings = useQueryOwnPayoutSettings(config);
  const queryExternalBankAccounts =
    queriesExternalBankAccounts.listExternalBankAccounts.useQuery(
      undefined,
      config
    );
  const queryInternalAccounts =
    queriesInternalAccounts.listInternalAccounts.useQuery(undefined, config);
  const queryMissingDataBanking = queriesOnboarding.getMissingFields.useQuery(
    { level: GetMissingFieldsLevelParameter.BANKING },
    config
  );
  const queryFeatureFlags = useFeatureFlags(config);

  return (onboardingModule, contextValue) =>
    buildOnboardingStep({
      getConfig: getPayoutMethodStepConfig,
      module: onboardingModule,
      context: contextValue,
      queries: [
        queryPayoutSettings as unknown as WSQueryResult<
          PayoutSettings,
          WSServiceError
        >,
        queryExternalBankAccounts as unknown as WSQueryResult<
          ExternalBankAccountList,
          WSServiceError
        >,
        queryInternalAccounts as unknown as WSQueryResult<
          InternalAccountList,
          WSServiceError
        >,
        queryMissingDataBanking,
        queryFeatureFlags
      ]
    });
};
